import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import LoadingSpinner from './common/LoadingSpinner';
import { formatRial } from '../utils/formatMoneyFa';
import { getApiBaseUrl } from '../utils/getApiUrl';

const toPersianDigits = (s: string | number): string =>
  String(s).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

export type InsuranceKind = 'paye' | 'takmili' | 'done' | 'waiting';

export interface InsuranceOrderRow {
  database?: string;
  patient_id?: number;
  patient_rec_code: string;
  recept_date: string;
  full_name: string;
  service: string;
  machine: string;
  insu_paye_name: string;
  insu_takmili_name: string;
  amount: number;
  status?: string;
  status_label?: string;
  dowork_date?: string;
}

interface ReceptionService {
  line_id?: number;
  service: string;
  machine: string;
  price_a_fran: number;
  price_a_insu: number;
  price_b_fran: number;
  price_b_insu: number;
  bime: number;
  fran: number;
  total: number;
  status: string;
  status_label: string;
  dowork_date?: string;
}

interface ReceptionCashRow {
  cash_id?: number;
  cash_date: string;
  amount: number;
  cashier_name: string;
  cash_register?: string;
  kind?: string;
  kind_label?: string;
  paid_status?: string;
  paid_label?: string;
}

interface ReceptionDetail {
  database: string;
  patient_id: number;
  patient_rec_code: string;
  recept_date: string;
  full_name: string;
  phone?: string;
  national_id?: string;
  insu_paye_name: string;
  insu_takmili_name: string;
  doctor_name?: string;
  operator_name?: string;
  note?: string;
  status: string;
  status_label: string;
  naghdi: number;
  bime: number;
  fran: number;
  payable: number;
  locked?: boolean;
  services: ReceptionService[];
  cash_rows: ReceptionCashRow[];
}

interface Props {
  open: boolean;
  kind: InsuranceKind | null;
  fromDate: string;
  toDate: string;
  title?: string;
  serviceTypesQuery?: string;
  onClose: () => void;
}

const KIND_LABEL: Record<InsuranceKind, string> = {
  paye: 'بیمه پایه',
  takmili: 'بیمه تکمیلی',
  done: 'انجام‌شده',
  waiting: 'در انتظار انجام',
};

const PAGE_SIZE = 50;

const statusBadgeClass = (status?: string): string => {
  if (status === 'done') return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200';
  if (status === 'partial') return 'bg-sky-50 text-sky-700 ring-1 ring-sky-200';
  return 'bg-amber-50 text-amber-800 ring-1 ring-amber-200';
};

const WatchdogInsuranceOrdersModal: React.FC<Props> = ({
  open,
  kind,
  fromDate,
  toDate,
  title,
  serviceTypesQuery,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orders, setOrders] = useState<InsuranceOrderRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amountDone, setAmountDone] = useState(0);
  const [amountWaiting, setAmountWaiting] = useState(0);
  const [totalDone, setTotalDone] = useState(0);
  const [totalWaiting, setTotalWaiting] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ReceptionDetail | null>(null);

  useEffect(() => {
    setPage(1);
  }, [kind, fromDate, toDate, serviceTypesQuery]);

  useEffect(() => {
    if (!open) {
      setPage(1);
      setOrders([]);
      setError(null);
      setDetailOpen(false);
      setDetail(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !kind || !fromDate || !toDate) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      setOrders([]);
      setDetailOpen(false);
      setDetail(null);
      try {
        const token = localStorage.getItem('admin_token') || '';
        const params = new URLSearchParams({
          kind,
          from_date: fromDate,
          to_date: toDate,
          page: String(page),
          page_size: String(PAGE_SIZE),
        });
        if (serviceTypesQuery) params.set('service_types', serviceTypesQuery);
        const res = await fetch(
          `${getApiBaseUrl()}/admin/customer-club/watchdog/insurance-orders?${params}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            typeof body.detail === 'string' ? body.detail : 'خطا در دریافت لیست سفارش‌ها'
          );
        }
        const data = await res.json();
        if (cancelled) return;
        setOrders((data.orders || []) as InsuranceOrderRow[]);
        setTotalCount(Number(data.total) || 0);
        setTotalAmount(Number(data.total_amount) || 0);
        setAmountDone(Number(data.amount_done) || 0);
        setAmountWaiting(Number(data.amount_waiting) || 0);
        setTotalDone(Number(data.total_done) || 0);
        setTotalWaiting(Number(data.total_waiting) || 0);
        setPages(Math.max(1, Number(data.pages) || 1));
        const serverPage = Number(data.page) || page;
        if (serverPage !== page) setPage(serverPage);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'خطا در دریافت لیست سفارش‌ها');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [open, kind, fromDate, toDate, serviceTypesQuery, page]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (detailOpen) {
        setDetailOpen(false);
        setDetail(null);
        setDetailError(null);
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, detailOpen]);

  const openDetail = async (row: InsuranceOrderRow) => {
    if (!row.patient_id) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    try {
      const token = localStorage.getItem('admin_token') || '';
      const params = new URLSearchParams({
        patient_id: String(row.patient_id),
      });
      if (row.database) params.set('database', row.database);
      const res = await fetch(
        `${getApiBaseUrl()}/admin/customer-club/watchdog/reception-detail?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          typeof body.detail === 'string' ? body.detail : 'خطا در دریافت ریز نوبت'
        );
      }
      setDetail((await res.json()) as ReceptionDetail);
    } catch (err: unknown) {
      setDetailError(err instanceof Error ? err.message : 'خطا در دریافت ریز نوبت');
    } finally {
      setDetailLoading(false);
    }
  };

  if (!open || !kind) return null;

  const label = KIND_LABEL[kind];

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-3 sm:p-6">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
        aria-label="بستن"
        onClick={onClose}
      />
      <div
        className="relative flex max-h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-sky-100"
        dir="rtl"
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-100 bg-gradient-to-l from-sky-50 to-white px-5 py-4">
          <div>
            <p className="text-xs text-sky-700/80">{title || 'لیست سفارش‌ها'}</p>
            <h2 className="mt-0.5 text-lg font-bold text-slate-900">{label}</h2>
            <p className="mt-1 text-xs text-slate-500">
              {toPersianDigits(fromDate)}
              {fromDate !== toDate ? ` تا ${toPersianDigits(toDate)}` : ''}
              {!loading && !error ? (
                <>
                  {' '}
                  · {toPersianDigits(totalCount)} سفارش · {formatRial(totalAmount)} ریال
                </>
              ) : null}
            </p>
            {!loading && !error && totalCount > 0 && (kind === 'paye' || kind === 'takmili') ? (
              <p className="mt-1 text-[11px] text-slate-500">
                انجام‌شده: {toPersianDigits(totalDone)} · {formatRial(amountDone)}
                {'  '}·{'  '}
                در انتظار: {toPersianDigits(totalWaiting)} · {formatRial(amountWaiting)}
              </p>
            ) : null}
            <p className="mt-1 text-[11px] text-slate-400">برای دیدن ریز نوبت روی هر ردیف کلیک کنید</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
          >
            بستن
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-auto px-3 py-3 sm:px-5">
          {loading ? (
            <div className="py-16">
              <LoadingSpinner text="در حال بارگذاری سفارش‌ها..." />
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-center text-sm text-red-800">
              {error}
            </div>
          ) : orders.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-12 text-center text-sm text-slate-600">
              سفارشی در بازه انتخابی نیست.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-100">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50 text-[11px] text-slate-500">
                  <tr>
                    <th className="whitespace-nowrap px-3 py-2.5 text-right font-medium">کد پذیرش</th>
                    <th className="whitespace-nowrap px-3 py-2.5 text-right font-medium">بیمار</th>
                    <th className="whitespace-nowrap px-3 py-2.5 text-right font-medium">تاریخ</th>
                    <th className="whitespace-nowrap px-3 py-2.5 text-right font-medium">وضعیت</th>
                    <th className="whitespace-nowrap px-3 py-2.5 text-right font-medium">خدمت</th>
                    <th className="whitespace-nowrap px-3 py-2.5 text-right font-medium">بیمه پایه</th>
                    <th className="whitespace-nowrap px-3 py-2.5 text-right font-medium">تکمیلی</th>
                    <th className="whitespace-nowrap px-3 py-2.5 text-right font-medium">مبلغ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 bg-white">
                  {orders.map((row, index) => {
                    const done = row.status === 'done';
                    return (
                      <tr
                        key={`${row.patient_id}-${row.recept_date}-${index}`}
                        className="cursor-pointer hover:bg-sky-50/70"
                        onClick={() => void openDetail(row)}
                      >
                        <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-700">
                          {toPersianDigits(row.patient_rec_code || '—')}
                        </td>
                        <td className="max-w-[10rem] truncate px-3 py-2 text-slate-800">
                          {row.full_name || '—'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-600">
                          {toPersianDigits(row.recept_date || '—')}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <span
                            className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${statusBadgeClass(row.status)}`}
                            title={
                              done && row.dowork_date
                                ? `انجام‌کار: ${toPersianDigits(row.dowork_date)}`
                                : undefined
                            }
                          >
                            {row.status_label || (done ? 'انجام‌شده' : 'در انتظار انجام')}
                          </span>
                        </td>
                        <td className="max-w-[12rem] truncate px-3 py-2 text-slate-700" title={row.service}>
                          {row.service || row.machine || '—'}
                        </td>
                        <td className="max-w-[8rem] truncate px-3 py-2 text-slate-600">
                          {row.insu_paye_name || '—'}
                        </td>
                        <td className="max-w-[8rem] truncate px-3 py-2 text-slate-600">
                          {row.insu_takmili_name || '—'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-left tabular-nums font-medium text-sky-900">
                          {formatRial(row.amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && !error && totalCount > 0 ? (
          <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 bg-slate-50/80 px-4 py-3 text-sm">
            <p className="text-xs text-slate-500">
              صفحه {toPersianDigits(page)} از {toPersianDigits(pages)}
              {' · '}
              نمایش {toPersianDigits(orders.length)} از {toPersianDigits(totalCount)}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-slate-700 enabled:hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                قبلی
              </button>
              <button
                type="button"
                disabled={page >= pages || loading}
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-slate-700 enabled:hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                بعدی
              </button>
            </div>
          </footer>
        ) : null}
      </div>

      {detailOpen ? (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-3 sm:p-6">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/45"
            aria-label="بستن ریز نوبت"
            onClick={() => {
              setDetailOpen(false);
              setDetail(null);
              setDetailError(null);
            }}
          />
          <div
            className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-indigo-100"
            dir="rtl"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-start justify-between gap-3 border-b border-slate-100 bg-gradient-to-l from-indigo-50 to-white px-5 py-4">
              <div>
                <p className="text-xs text-indigo-700/80">ریز نوبت / پذیرش</p>
                <h3 className="mt-0.5 text-lg font-bold text-slate-900">
                  {detail?.full_name || (detailLoading ? '...' : '—')}
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  کد پذیرش{' '}
                  {toPersianDigits(detail?.patient_rec_code || '—')}
                  {detail?.recept_date ? ` · ${toPersianDigits(detail.recept_date)}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setDetailOpen(false);
                  setDetail(null);
                  setDetailError(null);
                }}
                className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
              >
                بستن
              </button>
            </header>

            <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
              {detailLoading ? (
                <div className="py-14">
                  <LoadingSpinner text="در حال بارگذاری ریز نوبت..." />
                </div>
              ) : detailError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-center text-sm text-red-800">
                  {detailError}
                </div>
              ) : detail ? (
                <div className="space-y-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ${statusBadgeClass(detail.status)}`}
                    >
                      {detail.status_label}
                    </span>
                    {detail.locked ? (
                      <span className="inline-flex items-center rounded-md bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 ring-1 ring-rose-200">
                        قفل حساب
                      </span>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div className="rounded-xl border border-amber-100 bg-amber-50/50 px-3 py-2.5">
                      <p className="text-[11px] text-amber-800/80">نقدی صندوق</p>
                      <p className="mt-1 text-sm font-bold tabular-nums text-amber-900">
                        {formatRial(detail.naghdi)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-sky-100 bg-sky-50/50 px-3 py-2.5">
                      <p className="text-[11px] text-sky-800/80">بیمه</p>
                      <p className="mt-1 text-sm font-bold tabular-nums text-sky-900">
                        {formatRial(detail.bime)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-violet-100 bg-violet-50/50 px-3 py-2.5">
                      <p className="text-[11px] text-violet-800/80">فرانشیز</p>
                      <p className="mt-1 text-sm font-bold tabular-nums text-violet-900">
                        {formatRial(detail.fran)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                      <p className="text-[11px] text-slate-600">قابل پرداخت</p>
                      <p className="mt-1 text-sm font-bold tabular-nums text-slate-900">
                        {formatRial(detail.payable)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                    <p>
                      <span className="text-slate-500">بیمه پایه: </span>
                      <span className="font-medium text-slate-800">
                        {detail.insu_paye_name || '—'}
                      </span>
                    </p>
                    <p>
                      <span className="text-slate-500">بیمه تکمیلی: </span>
                      <span className="font-medium text-slate-800">
                        {detail.insu_takmili_name || '—'}
                      </span>
                    </p>
                    <p>
                      <span className="text-slate-500">پزشک: </span>
                      <span className="font-medium text-slate-800">
                        {detail.doctor_name || '—'}
                      </span>
                    </p>
                    <p>
                      <span className="text-slate-500">ثبت‌کننده پذیرش: </span>
                      <span className="font-medium text-slate-800">
                        {detail.operator_name || '—'}
                      </span>
                    </p>
                    <p>
                      <span className="text-slate-500">کد ملی: </span>
                      <span className="font-medium tabular-nums text-slate-800">
                        {detail.national_id
                          ? toPersianDigits(detail.national_id)
                          : '—'}
                      </span>
                    </p>
                    <p>
                      <span className="text-slate-500">موبایل: </span>
                      <span className="font-medium tabular-nums text-slate-800">
                        {detail.phone ? toPersianDigits(detail.phone) : '—'}
                      </span>
                    </p>
                  </div>

                  <div className="rounded-xl border border-amber-100 bg-amber-50/40 px-3 py-2.5">
                    <p className="text-[11px] font-medium text-amber-900">یادداشت پذیرش</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-800">
                      {detail.note?.trim() ? detail.note : 'یادداشتی ثبت نشده'}
                    </p>
                  </div>

                  <section>
                    <h4 className="text-sm font-bold text-slate-900">خدمات این پذیرش</h4>
                    <div className="mt-2 overflow-x-auto rounded-xl border border-slate-100">
                      <table className="min-w-full divide-y divide-slate-100 text-sm">
                        <thead className="bg-slate-50 text-[11px] text-slate-500">
                          <tr>
                            <th className="px-3 py-2 text-right font-medium">خدمت</th>
                            <th className="px-3 py-2 text-right font-medium">وضعیت</th>
                            <th className="px-3 py-2 text-right font-medium">بیمه</th>
                            <th className="px-3 py-2 text-right font-medium">فرانشیز</th>
                            <th className="px-3 py-2 text-right font-medium">جمع</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {(detail.services || []).map((svc) => (
                            <tr key={svc.line_id}>
                              <td className="max-w-[14rem] truncate px-3 py-2 text-slate-800" title={svc.service}>
                                {svc.service || svc.machine || '—'}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2">
                                <span
                                  className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-medium ${statusBadgeClass(svc.status)}`}
                                  title={
                                    svc.dowork_date
                                      ? toPersianDigits(svc.dowork_date)
                                      : undefined
                                  }
                                >
                                  {svc.status_label}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 tabular-nums text-sky-800">
                                {formatRial(svc.bime)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 tabular-nums text-violet-800">
                                {formatRial(svc.fran)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 tabular-nums font-medium text-slate-900">
                                {formatRial(svc.total)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  {(detail.cash_rows || []).length > 0 ? (
                    <section>
                      <h4 className="text-sm font-bold text-slate-900">ردیف‌های صندوق</h4>
                      <div className="mt-2 overflow-x-auto rounded-xl border border-slate-100">
                        <table className="min-w-full divide-y divide-slate-100 text-sm">
                          <thead className="bg-slate-50 text-[11px] text-slate-500">
                            <tr>
                              <th className="px-3 py-2 text-right font-medium">تاریخ</th>
                              <th className="px-3 py-2 text-right font-medium">نوع</th>
                              <th className="px-3 py-2 text-right font-medium">وضعیت پول</th>
                              <th className="px-3 py-2 text-right font-medium">صندوق‌دار</th>
                              <th className="px-3 py-2 text-right font-medium">مبلغ</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {detail.cash_rows.map((cash, idx) => (
                              <tr key={`${cash.cash_id}-${idx}`}>
                                <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-600">
                                  {toPersianDigits(cash.cash_date || '—')}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                                  {cash.kind_label || '—'}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-[11px] text-slate-600">
                                  {cash.paid_label || '—'}
                                </td>
                                <td className="px-3 py-2 text-slate-700">
                                  {cash.cashier_name || '—'}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 tabular-nums font-medium text-amber-900">
                                  {formatRial(cash.amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>,
    document.body
  );
};

export default WatchdogInsuranceOrdersModal;
